import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Container,
  Paper,
  ThemeIcon,
  Group,
  Stack,
  Table,
  Badge,
  SimpleGrid,
  Grid,
  Button,
  Divider,
  Box,
  rem,
  Card,
  Loader,
  Center,
  ScrollArea,
  Select
} from '@mantine/core';
import {
  IconLayoutDashboard,
  IconBuildingWarehouse,
  IconPackage,
  IconArrowRight,
  IconClock,
  IconAlertCircle,
  IconRefresh,
  IconSettings,
  IconSearch
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import api from '../../api/axiosConfig';

const heroGradient = 'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 35%, rgba(37,99,235,0.88) 100%)';

export default function WarehouseDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Temel Data State'leri
  const [warehouses, setWarehouses] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [stats, setStats] = useState({ totalWarehouses: 0, totalProducts: 0 });
  
  // Arama özelliği için gerekli tüm veri state'leri
  const [allProducts, setAllProducts] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [searchedProductId, setSearchedProductId] = useState(null);
  
  // Admin yetki kontrolü için state
  const [isAdmin, setIsAdmin] = useState(false); 

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Backend'deki ilgili tüm verileri paralel çekiyoruz
      const [whRes, prodRes, txRes] = await Promise.all([
        api.get('/warehouse/warehouses/all'),
        api.get('/warehouse/products/all'),
        api.get('/warehouse/transactions/all')
      ]);

      setWarehouses(whRes.data || []);
      setAllProducts(prodRes.data || []);
      setAllTransactions(txRes.data || []);
      setRecentTransactions(txRes.data ? txRes.data.slice(0, 5) : []); // Son 5 hareket
      
      setStats({
        totalWarehouses: whRes.data?.length || 0,
        totalProducts: prodRes.data?.length || 0
      });
    } catch (err) {
      notifications.show({
        title: 'Sistem Hatası',
        message: 'Depo yönetim paneli verileri yüklenemedi.',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // JWT Token'dan rolü oku
    try {
      const token = localStorage.getItem('token'); 
      if (token) {
        const payloadBase64 = token.split('.')[1]; 
        const decodedJson = atob(payloadBase64); 
        const payload = JSON.parse(decodedJson);
        
        if (payload.role === 'admin') {
          setIsAdmin(true);
        }
      }
    } catch (error) {
      console.error('Token çözümlenirken hata oluştu:', error);
    }

    fetchDashboardData();
  }, []);

  // --- GLOBAL STOK ARAMA MANTIĞI ---
  const searchResults = useMemo(() => {
    if (!searchedProductId) return null;
    
    const targetProdId = Number(searchedProductId);
    const selectedProd = allProducts.find(p => p.id === targetProdId);
    if (!selectedProd) return null;

    // Tüm depolar için başlangıç stokunu 0 olarak ayarla
    const stockMap = {};
    warehouses.forEach(wh => {
      stockMap[wh.id] = { name: wh.name, stock: 0 };
    });

    // Bu ürüne ait tüm hareketleri tarayıp depolara göre topla/çıkar
    allTransactions.forEach(tx => {
      if (tx.product_id === targetProdId && stockMap[tx.warehouse_id]) {
        stockMap[tx.warehouse_id].stock += tx.transaction_type === 'GİRİŞ' ? tx.quantity : -tx.quantity;
      }
    });

    // Sadece stoğu 0'dan büyük olan depoları filtrele
    const stocksInWarehouses = Object.values(stockMap).filter(w => w.stock > 0);

    return {
      product: selectedProd,
      stocks: stocksInWarehouses
    };
  }, [searchedProductId, allProducts, allTransactions, warehouses]);


  return (
    <Container fluid p={{ base: 'sm', md: 'lg' }}>
      <Stack gap="xl">
        
        {/* KAHRAMAN ÜST PANEL (HERO) */}
        <Paper
          radius="xl"
          p={{ base: 'lg', md: 'xl' }}
          style={{
            background: heroGradient,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
            border: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Group gap="sm" align="center">
                <ThemeIcon size={42} radius="xl" variant="white" color="blue">
                  <IconLayoutDashboard size={22} />
                </ThemeIcon>
                <Stack gap={2}>
                  <Text size="sm" fw={600} c="rgba(255,255,255,0.8)">Depo Modülü</Text>
                  <Title order={1} fw={900} c="white">Depo ve Stok Yönetim Paneli</Title>
                </Stack>
              </Group>

              <Group gap="sm">
                {/* SADECE ADMINLERE GÖRÜNEN AYARLAR BUTONU */}
                {isAdmin && (
                  <Button 
                    variant="white" 
                    color="dark" 
                    radius="xl" 
                    size="xs"
                    leftSection={<IconSettings size={14} />}
                    onClick={() => navigate('/warehouse/settings')} 
                    style={{ background: 'rgba(255,255,255,0.14)', color: 'white', border: 'none' }}
                  >
                    Ayarlar
                  </Button>
                )}

                <Button 
                  variant="white" 
                  radius="xl" 
                  size="xs"
                  leftSection={<IconRefresh size={14} />}
                  loading={loading}
                  onClick={fetchDashboardData}
                  style={{ background: 'rgba(255,255,255,0.14)', color: 'white', border: 'none' }}
                >
                  Yenile
                </Button>
              </Group>

            </Group>
            <Text c="rgba(255,255,255,0.78)" size="sm" maw={800}>
              Fiziksel depolarınızın durumunu inceleyin, malzeme envanter kartlarını yönetin ve malzeme giriş/çıkış süreçleri ile irsaliye kayıtlarını kurumsal tasarım standartlarında takip edin.
            </Text>
          </Stack>
        </Paper>

        {loading ? (
          <Card withBorder radius="xl" p="xl" h={300}>
            <Center h="100%">
              <Stack align="center" gap="xs">
                <Loader size="lg" type="dots" />
                <Text size="sm" c="dimmed">Depo analizleri ve envanter raporları hazırlanıyor...</Text>
              </Stack>
            </Center>
          </Card>
        ) : (
          <>
            {/* METRİK KARTLARI (2 Kolonlu Ferah Tasarım) */}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
              <Paper radius="xl" p="xl" withBorder shadow="sm" style={{ borderTop: '4px solid var(--mantine-color-blue-6)' }}>
                <Group justify="space-between" align="flex-start">
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">Aktif Depolar</Text>
                    <Text fw={900} size={rem(36)}>{stats.totalWarehouses} <Text component="span" size="md" c="dimmed" fw={600}>Lokasyon</Text></Text>
                  </Stack>
                  <ThemeIcon radius="xl" variant="light" color="blue" size={54}>
                    <IconBuildingWarehouse size={28} />
                  </ThemeIcon>
                </Group>
                <Text size="sm" c="dimmed" mt="md">Sistemde tanımlı ve operasyona açık fiziksel depo ve ambar noktaları.</Text>
              </Paper>

              <Paper radius="xl" p="xl" withBorder shadow="sm" style={{ borderTop: '4px solid var(--mantine-color-green-6)' }}>
                <Group justify="space-between" align="flex-start">
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">Tanımlı Stok Kartı</Text>
                    <Text fw={900} size={rem(36)}>{stats.totalProducts} <Text component="span" size="md" c="dimmed" fw={600}>Çeşit</Text></Text>
                  </Stack>
                  <ThemeIcon radius="xl" variant="light" color="green" size={54}>
                    <IconPackage size={28} />
                  </ThemeIcon>
                </Group>
                <Text size="sm" c="dimmed" mt="md">Sisteme tanımlanmış benzersiz barkoda sahip aktif ürün/malzeme kartları.</Text>
              </Paper>
            </SimpleGrid>

            {/* GLOBAL STOK ARAMA BÖLÜMÜ (SADECE ADMİNLERE ÖZEL) */}
            {isAdmin && (
              <Paper radius="xl" p={{ base: 'md', md: 'xl' }} withBorder shadow="sm">
                <Group gap="xs" mb="sm">
                  <ThemeIcon variant="subtle" color="blue"><IconSearch size={20} /></ThemeIcon>
                  <Text fw={700} size="lg">Global Stok Sorgulama</Text>
                </Group>
                <Text size="sm" c="dimmed" mb="lg">
                  Sistemdeki tüm depolar üzerinde arama yapın. Hangi üründen hangi depoda ne kadar kaldığını anında görüntüleyin.
                </Text>
                
                <Select
                  placeholder="Listeden malzeme adına veya barkoduna göre arayın..."
                  data={allProducts.map(p => ({ value: p.id.toString(), label: `${p.name} (${p.barcode})` }))}
                  searchable
                  clearable
                  size="md"
                  radius="md"
                  leftSection={<IconSearch size={16} />}
                  value={searchedProductId}
                  onChange={setSearchedProductId}
                />

                {/* Arama Sonuç Alanı */}
                {searchResults && (
                  <Box mt="xl" p="md" bg="gray.0" style={{ borderRadius: '12px', border: '1px solid var(--mantine-color-gray-3)' }}>
                    <Text fw={800} size="md" mb="sm" c="blue.9">
                      {searchResults.product.name} <Text component="span" fw={500} c="dimmed">({searchResults.product.barcode})</Text> Dağılımı
                    </Text>
                    
                    {searchResults.stocks.length === 0 ? (
                      <Group gap="xs">
                        <IconAlertCircle size={18} color="var(--mantine-color-red-6)" />
                        <Text size="sm" c="red" fw={600}>Bu ürün şu anda hiçbir depoda bulunmamaktadır (Stok 0).</Text>
                      </Group>
                    ) : (
                      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
                        {searchResults.stocks.map((st, idx) => (
                          <Card key={idx} shadow="sm" radius="md" withBorder p="md" bg="white">
                            <Group justify="space-between">
                              <Text size="xs" c="dimmed" fw={700} tt="uppercase">{st.name}</Text>
                              <ThemeIcon size="sm" variant="light" color="green"><IconBuildingWarehouse size={12} /></ThemeIcon>
                            </Group>
                            <Text fw={900} size="xl" mt="sm" c="green.8">
                              {st.stock} <Text component="span" size="sm" fw={600} c="gray.6">{searchResults.product.unit}</Text>
                            </Text>
                          </Card>
                        ))}
                      </SimpleGrid>
                    )}
                  </Box>
                )}
              </Paper>
            )}

            {/* DEPOLAR VE SON HAREKETLER GRID */}
            <Grid columns={12} gutter="lg">
              
              {/* SOL SÜTUN: DEPOLAR LİSTESİ (Admin ise 5 kolon, normal kullanıcı ise tam genişlik 12 kolon) */}
              <Grid.Col span={{ base: 12, md: isAdmin ? 5 : 12 }}>
                <Paper radius="xl" p="xl" withBorder shadow="sm" h="100%">
                  <Text fw={700} size="lg" mb="md">Fiziksel Depolar</Text>
                  <Divider mb="md" />
                  <Stack gap="sm">
                    {warehouses.length === 0 ? (
                      <Text size="sm" c="dimmed" fs="italic">Tanımlı depo bulunamadı.</Text>
                    ) : (
                      warehouses.map((wh) => (
                        <Card 
                          key={wh.id} 
                          withBorder 
                          radius="md" 
                          p="sm" 
                          style={{ cursor: 'pointer', transition: '0.2s' }}
                          className="hover:bg-gray-50"
                          onClick={() => navigate(`/warehouse/${wh.id}`)}
                        >
                          <Group justify="space-between">
                            <Box>
                              <Text fw={600} size="sm">{wh.name}</Text>
                              <Text size="xs" c="dimmed">{wh.location || 'Lokasyon Belirtilmemiş'}</Text>
                            </Box>
                            <Button variant="subtle" size="xs" rightSection={<IconArrowRight size={14} />}>
                              Yönet
                            </Button>
                          </Group>
                        </Card>
                      ))
                    )}
                  </Stack>
                </Paper>
              </Grid.Col>

              {/* SAĞ SÜTUN: SON STOK HAREKETLERİ (SADECE ADMİNLERE ÖZEL) */}
              {isAdmin && (
                <Grid.Col span={{ base: 12, md: 7 }}>
                  <Paper radius="xl" p="xl" withBorder shadow="sm" h="100%">
                    <Group gap="xs" mb="lg">
                      <ThemeIcon variant="subtle" color="gray"><IconClock size={20} /></ThemeIcon>
                      <Text fw={700} size="lg">Son Stok Hareketleri</Text>
                    </Group>
                    <Divider mb="md" />
                    
                    {recentTransactions.length === 0 ? (
                      <Center h={200}>
                        <Text size="sm" c="dimmed" fs="italic">Henüz bir stok hareketi kaydedilmemiş.</Text>
                      </Center>
                    ) : (
                      <ScrollArea>
                        <Table verticalSpacing="md" highlightOnHover striped>
                          <Table.Thead bg="var(--mantine-color-gray-0)">
                            <Table.Tr>
                              <Table.Th>Malzeme</Table.Th>
                              <Table.Th>İşlem Türü</Table.Th>
                              <Table.Th>Miktar</Table.Th>
                              <Table.Th>Tarih</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {recentTransactions.map((tx) => (
                              <Table.Tr key={tx.id}>
                                <Table.Td fw={600}>{tx.product?.name || `Ürün ID: ${tx.product_id}`}</Table.Td>
                                <Table.Td>
                                  <Badge color={tx.transaction_type === 'GİRİŞ' ? 'green' : 'red'} variant="light">
                                    {tx.transaction_type}
                                  </Badge>
                                </Table.Td>
                                <Table.Td fw={700}>{tx.quantity} {tx.product?.unit || 'Adet'}</Table.Td>
                                <Table.Td c="dimmed" size="xs">
                                  {new Date(tx.date).toLocaleString('tr-TR', { 
                                    timeZone: 'Europe/Istanbul',
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                    )}
                  </Paper>
                </Grid.Col>
              )}

            </Grid>
          </>
        )}
      </Stack>
    </Container>
  );
}

